const factBoxes = document.querySelectorAll('[data-fact-box]');

const animationDuration = 350;
const animationEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';

const cubicBezier = (x1, y1, x2, y2) => {
    const a = (a1, a2) => 1 - 3 * a2 + 3 * a1;
    const b = (a1, a2) => 3 * a2 - 6 * a1;
    const c = a1 => 3 * a1;

    const calculate = (t, a1, a2) => ((a(a1, a2) * t + b(a1, a2)) * t + c(a1)) * t;

    const slope = (t, a1, a2) => 3 * a(a1, a2) * t * t + 2 * b(a1, a2) * t + c(a1);

    return progress => {
        if (progress <= 0 || progress >= 1) {
            return progress;
        }

        let t = progress;

        for (let i = 0; i < 5; i++) {
            const currentSlope = slope(t, x1, x2);

            if (currentSlope === 0) {
                break;
            }

            t -= (calculate(t, x1, x2) - progress) / currentSlope;
        }

        return calculate(t, y1, y2);
    };
};

const easeAnimation = cubicBezier(0.4, 0, 0.2, 1);

const focusableSelector = ['a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])', 'textarea:not([disabled])', 'summary', 'iframe', 'object', 'embed', 'audio[controls]', 'video[controls]', '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not([tabindex="-1"])',].join(',');

const reducedMotionMediaQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : {matches: false};

const getCssPixels = (element, propertyName) => {
    const value = Number.parseFloat(window.getComputedStyle(element).getPropertyValue(propertyName));
    return Number.isFinite(value) ? value : 0;
};

factBoxes.forEach(factBox => {
    const content = factBox.querySelector('[data-fact-box-content]');
    const actions = factBox.querySelector('[data-fact-box-actions]');
    const toggle = factBox.querySelector('[data-fact-box-toggle]');
    const toggleText = toggle?.querySelector('[data-fact-box-toggle-text]');

    // The toggle is deliberately omitted in Optimizely edit mode.
    if (!content || !actions || !toggle || !toggleText) {
        return;
    }

    if (factBox.dataset.factBoxInitialized === 'true') {
        return;
    }

    factBox.dataset.factBoxInitialized = 'true';

    const suppressedTabIndexes = new Map();
    const suppressedAriaHidden = new Map();
    let resizeFrame;
    let expandedBeforePrint;
    let heightAnimation;
    let scrollFrame;

    const cancelScrollAnimation = () => {
        if (scrollFrame) {
            window.cancelAnimationFrame(scrollFrame);
            scrollFrame = undefined;
        }
    };

    const scrollToStart = startTime => {
        cancelScrollAnimation();

        const startScrollY = window.scrollY;
        const scrollMargin = getCssPixels(factBox, 'scroll-margin-block-start');

        const targetScrollY = Math.max(0, startScrollY + factBox.getBoundingClientRect().top - scrollMargin);

        const distance = targetScrollY - startScrollY;

        const setScrollPosition = top => {
            window.scrollTo({
                top,
                behavior: 'instant',
            });
        };

        if (reducedMotionMediaQuery.matches || Math.abs(distance) < 1) {
            setScrollPosition(targetScrollY);
            return;
        }

        const animateScroll = currentTime => {
            const progress = Math.min((currentTime - startTime) / animationDuration, 1);

            const easedProgress = easeAnimation(progress);

            setScrollPosition(startScrollY + distance * easedProgress);

            if (progress < 1) {
                scrollFrame = window.requestAnimationFrame(animateScroll);
            } else {
                scrollFrame = undefined;
            }
        };

        // Run the first scroll frame immediately.
        animateScroll(startTime);
    };

    const restoreCollapsedContent = () => {
        suppressedTabIndexes.forEach((tabIndex, element) => {
            if (tabIndex === null) {
                element.removeAttribute('tabindex');
            } else {
                element.setAttribute('tabindex', tabIndex);
            }
        });
        suppressedTabIndexes.clear();

        suppressedAriaHidden.forEach((ariaHidden, element) => {
            if (ariaHidden === null) {
                element.removeAttribute('aria-hidden');
            } else {
                element.setAttribute('aria-hidden', ariaHidden);
            }
        });
        suppressedAriaHidden.clear();
    };

    const hideFromAssistiveTechnology = element => {
        if (!suppressedAriaHidden.has(element)) {
            suppressedAriaHidden.set(element, element.getAttribute('aria-hidden'));
        }

        element.setAttribute('aria-hidden', 'true');
    };

    const suppressClippedContent = clippedContentBottom => {
        restoreCollapsedContent();

        if (!factBox.classList.contains('factBox--collapsed')) {
            return;
        }

        const visibleContentBottom = clippedContentBottom ?? actions.getBoundingClientRect().top;

        content
            .querySelectorAll(focusableSelector)
            .forEach(element => {
                if (element.getBoundingClientRect().bottom <= visibleContentBottom) {
                    return;
                }

                suppressedTabIndexes.set(element, element.getAttribute('tabindex'));
                element.setAttribute('tabindex', '-1');
                hideFromAssistiveTechnology(element);
            });

        const hideClippedElements = parent => {
            Array.from(parent.children).forEach(element => {
                const bounds = element.getBoundingClientRect();

                if (bounds.top >= visibleContentBottom) {
                    hideFromAssistiveTechnology(element);
                } else if (bounds.bottom > visibleContentBottom) {
                    hideClippedElements(element);
                }
            });
        };

        hideClippedElements(content);
    };

    const applyExpandedState = (expanded, clippedContentBottom) => {
        factBox.classList.toggle('factBox--collapsed', !expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.setAttribute('aria-label', expanded ? toggle.dataset.showLessLabel : toggle.dataset.showMoreLabel);
        toggleText.textContent = expanded ? toggle.dataset.showLess : toggle.dataset.showMore;

        if (expanded) {
            restoreCollapsedContent();
        } else {
            factBox.scrollTop = 0;
            suppressClippedContent(clippedContentBottom);
        }
    };

    const cancelHeightAnimation = () => {
        if (heightAnimation) {
            heightAnimation.cancel();
            heightAnimation = undefined;
        }

        factBox.style.removeProperty('height');
        factBox.classList.remove('factBox--animating');
    };

    const measureTargetLayout = expanded => {
        const wasCollapsed = factBox.classList.contains('factBox--collapsed');
        factBox.classList.toggle('factBox--collapsed', !expanded);

        const factBoxBounds = factBox.getBoundingClientRect();
        const layout = {
            height: factBoxBounds.height,
            clippedContentBottom: expanded ? undefined : actions.getBoundingClientRect().top,
        };

        factBox.classList.toggle('factBox--collapsed', wasCollapsed);
        return layout;
    };

    const setExpanded = (expanded, animate = false) => {
        const canAnimate = animate && !reducedMotionMediaQuery.matches && typeof factBox.animate === 'function';

        if (!canAnimate) {
            cancelHeightAnimation();
            applyExpandedState(expanded);
            return Promise.resolve();
        }

        const startHeight = factBox.getBoundingClientRect().height;
        cancelHeightAnimation();

        const targetLayout = measureTargetLayout(expanded);
        if (Math.abs(targetLayout.height - startHeight) < 1) {
            applyExpandedState(expanded);
            return Promise.resolve();
        }

        factBox.classList.add('factBox--animating');
        factBox.style.height = `${startHeight}px`;
        applyExpandedState(expanded, targetLayout.clippedContentBottom);

        const animation = factBox.animate([{
            height: `${startHeight}px`,
        }, {
            height: `${targetLayout.height}px`,
        },], {
            duration: animationDuration,
            easing: animationEasing,
            fill: 'both',
        });
        heightAnimation = animation;

        return animation.finished.then(() => {
            if (heightAnimation !== animation) {
                return;
            }

            factBox.classList.remove('factBox--animating');
            factBox.style.removeProperty('height');
            animation.cancel();
            heightAnimation = undefined;

            if (!expanded) {
                suppressClippedContent();
            }
        }, () => {});
    };

    const measureFullHeight = () => {
        factBox.classList.remove('factBox--collapsible', 'factBox--collapsed');
        actions.hidden = true;

        return factBox.getBoundingClientRect().height;
    };

    const update = () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
            cancelHeightAnimation();
            cancelScrollAnimation();

            const wasCollapsible = factBox.classList.contains('factBox--collapsible');
            const wasExpanded = wasCollapsible && !factBox.classList.contains('factBox--collapsed');
            const fullHeight = measureFullHeight();
            const triggerHeight = getCssPixels(factBox, '--fact-box-trigger-height');
            const shouldCollapse = triggerHeight > 0 && fullHeight > triggerHeight;

            restoreCollapsedContent();
            factBox.classList.toggle('factBox--collapsible', shouldCollapse);
            actions.hidden = !shouldCollapse;

            if (shouldCollapse) {
                setExpanded(wasCollapsible ? wasExpanded : false);
            } else {
                factBox.classList.remove('factBox--collapsed');
                toggle.setAttribute('aria-expanded', 'false');
                toggle.setAttribute('aria-label', toggle.dataset.showMoreLabel);
                toggleText.textContent = toggle.dataset.showMore;
            }
        });
    };

    toggle.addEventListener('click', () => {
        const nextExpanded = toggle.getAttribute('aria-expanded') !== 'true';

        cancelScrollAnimation();

        if (!nextExpanded && factBox.getBoundingClientRect().top >= 0) {
            setExpanded(false, true);
            return;
        }

        if (nextExpanded || reducedMotionMediaQuery.matches) {
            if (!nextExpanded) {
                const scrollMargin = getCssPixels(factBox, 'scroll-margin-block-start');
                const targetScrollY = Math.max(0, window.scrollY + factBox
                    .getBoundingClientRect().top - scrollMargin);

                window.scrollTo({
                    top: targetScrollY,
                    behavior: 'instant',
                });
            }

            setExpanded(nextExpanded, true);
            return;
        }

        window.requestAnimationFrame(startTime => {
            scrollToStart(startTime);
            setExpanded(false, true);
        });
    });

    if (typeof reducedMotionMediaQuery.addEventListener === 'function') {
        reducedMotionMediaQuery.addEventListener('change', event => {
            if (event.matches) {
                cancelHeightAnimation();
                cancelScrollAnimation();
            }
        });
    }

    window.addEventListener('resize', update);

    if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(content);
    }

    if ('MutationObserver' in window) {
        const mutationObserver = new MutationObserver(update);
        mutationObserver.observe(content, {
            childList: true,
            characterData: true,
            subtree: true,
        });
    }

    window.addEventListener('beforeprint', () => {
        if (!factBox.classList.contains('factBox--collapsible')) {
            return;
        }

        expandedBeforePrint = !factBox.classList.contains('factBox--collapsed');
        setExpanded(true);
    });

    window.addEventListener('afterprint', () => {
        if (expandedBeforePrint === undefined) {
            return;
        }

        setExpanded(expandedBeforePrint);
        expandedBeforePrint = undefined;
    });

    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(update);
    }

    update();
});
